import APIHelper from "@giscon/utils.api-helper-esri";
import { endpoints } from "../configs/config";

const _round = (number, decimalPlaces) => {
  if (number === null || number === undefined) return null;
  const pow = Math.pow(10, decimalPlaces);
  return Math.round(number * pow) / pow;
};

/**
 * Format a numeric value for display.
 *
 * Behavior summary:
 * - Rounds the input using the module `_round(number, decimalPlaces)` helper.
 * - Uses `toFixed` on the rounded absolute value so fractional digits are
 *   zero-padded to `decimalPlaces`.
 * - Inserts thousands separators every 3 digits and preserves the original
 *   sign so negative tiny values (e.g. -0.000) keep a minus sign.
 *
 * @param {number|null|undefined} number - The value to format. Returns empty
 *   string for `null`/`undefined`.
 * @param {number} decimalPlaces - Number of decimal places (non-negative
 *   integer).
 * @param {string} [COMMA=','] - Decimal separator to use in the output.
 * @param {string} [THOUSANDS_SEPARATOR=' '] - Character used as thousands
 *   separator (e.g. space or NBSP).
 * @returns {string} Formatted number string, e.g. `"-1 234,56"`.
 */
export const formatNumber = (
  number,
  decimalPlaces,
  COMMA = ",",
  THOUSANDS_SEPARATOR = " "
) => {
  if (number === null || number === undefined) return "";

  // Use module rounding helper to keep rounding behaviour consistent
  const rounded = _round(number, decimalPlaces);

  // Preserve sign from rounded value so tiny negatives rendered as zero do not show "-0,0"
  const sign = rounded < 0 ? "-" : "";

  // toFixed guarantees zero-padded fractional digits
  const absStr = Math.abs(Number(rounded)).toFixed(decimalPlaces);
  const [intPart, fracPart = ""] = absStr.split(".");

  // insert thousands separators into integer part (every 3 digits from right)
  const intWithSep = intPart.replace(
    /\B(?=(\d{3})+(?!\d))/g,
    THOUSANDS_SEPARATOR
  );

  return `${sign}${intWithSep}${
    decimalPlaces > 0 ? `${COMMA}${fracPart.padStart(decimalPlaces, "0")}` : ""
  }`;
};

/**
 * Helper function to get current filter values from CommunicationStore
 * @param {Object} CommunicationStore - The communication store
 * @returns {Object} Object containing Kategorie, Parameter, and Zeitabschnitt
 */
export const getCurrentFilterValues = (CommunicationStore) => {
  const getFilterConfig = CommunicationStore.getMessagesToID({
    to: "filterAccess",
  })?.message;

  const filterLevel = CommunicationStore.getMessagesToID({
    to: "filterLevel",
  })?.message;

  if (!getFilterConfig || !filterLevel || !Array.isArray(filterLevel)) {
    return {};
  }

  // Dynamically get values for all filter levels
  const result = {};
  filterLevel.forEach((level) => {
    const levelData = getFilterConfig(level);
    result[level] = levelData?.items?.[levelData?.index]?.label || null;
  });

  return result;
};

/**
 * Call backend API to get statistics
 * @param {Object} params - The parameters for the API call
 * @param {string} params.CategoryId - Kategorie value
 * @param {string} params.ParameterId - Parameter value
 * @param {string} params.SeasonId - Zeitabschnitt value
 * @param {number} params.ObjectId - Selected entity ObjectId from ZoomToGeo
 * @param {number} params.ServiceId - Selected source Id from ZoomToGeo
 * @returns {Promise} API response
 */
export async function fetchBackendStatistics({
  CategoryId,
  ParameterId,
  SeasonId,
  ObjectId,
  ServiceId,
}) {
  const apiUrl = endpoints.klimaStatisticsUrl;

  const response = await fetch(apiUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      CategoryId,
      ParameterId,
      SeasonId,
      ObjectId,
      ServiceId,
    }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

/** Helper: derive a display string for area of ServiceItem*/
const deriveArea = (decimalPlaces, geometry) => {
  return geometry
    ? round(
        APIHelper.planarArea(geometry, "square-kilometers"),
        decimalPlaces,
        false
      ) + " [km²]"
    : " - ";
};

/**
 * Extract and normalize data from a CalendarPeriod or Scenario object.
 * Handles label derivation, unit extraction, and projection metadata.
 */
const extractRowData = (item, opts = {}) => {
  const isPeriod = !!item?.CalendarPeriodId;
  const label =
    opts.label ??
    (isPeriod ? String(item.CalendarPeriodId).replace(/_/g, "-") : "");

  const scenario = (item.ScenarioName || item.ScenarioId || "")
    .toString()
    .toLowerCase();
  const isRcp26 =
    scenario.indexOf("2.6") >= 0 ||
    scenario.indexOf("rcp2.6") >= 0 ||
    scenario.indexOf("rcp26") >= 0;
  const isRcp85 =
    scenario.indexOf("8.5") >= 0 ||
    scenario.indexOf("rcp8.5") >= 0 ||
    scenario.indexOf("rcp85") >= 0;

  return {
    avg: item.Avg ?? null,
    min: item.Min ?? null,
    max: item.Max ?? null,
    count: item.Count ?? null,
    unit: opts.unit ?? item.Unit ?? "",
    label,
    rawLabel: opts.rawLabel ?? label,
    projection: opts.projection || false,
    scenarioName: item.ScenarioName || null,
    isRcp26: scenario ? isRcp26 : null,
    isRcp85: scenario ? isRcp85 : null,
  };
};

/**
 * Format extracted row data into the UI result shape.
 * This is a pure formatting function - all data extraction/normalization
 * should be done via extractRowData before calling this.
 *
 * @param {Object} data - Pre-extracted row data (avg, min, max, count, unit, label, rawLabel, projection, scenarioName, isRcp26, isRcp85)
 * @param {string} areaTxt - Pre-computed area label
 * @param {number} decimalPlaces - Number of decimal places for rounding
 * @param {Object} [options] - Formatting options
 * @param {boolean} [options.includeRange=true] - If true, avgTxt includes min-max range ("avg (min-max)"); if false, avgTxt is just the average value.
 * @returns {Object} Formatted row for UI display
 */
export const formatStatisticRow = (
  data,
  areaTxt,
  decimalPlaces = 2,
  { includeRange = true } = {}
) => {
  const {
    avg,
    min,
    max,
    count,
    unit,
    label,
    rawLabel,
    projection,
    scenarioName,
    isRcp26,
    isRcp85,
  } = data;
  const unitSuffix = unit ? ` ${unit}` : "";

  const avgTxtWithRange =
    avg !== null
      ? includeRange
        ? `${formatNumber(avg, decimalPlaces)} (${formatNumber(
            min,
            decimalPlaces
          )} - ${formatNumber(max, decimalPlaces)})${unitSuffix}`
        : `${formatNumber(avg, decimalPlaces)}${unitSuffix}`
      : "";

  return {
    area: areaTxt,
    avgTxt: avgTxtWithRange,
    minTxt: formatNumber(min, decimalPlaces) + unitSuffix,
    maxTxt: formatNumber(max, decimalPlaces) + unitSuffix,
    medTxt: formatNumber(avg, decimalPlaces) + unitSuffix,
    min,
    max,
    avg,
    med: avg,
    count: count,
    units: unit || "",
    meta: {
      short: label,
      label,
      rawLabel: rawLabel || label,
      projection: projection,
      scenarioName: scenarioName,
      isRcp26: isRcp26,
      isRcp85: isRcp85,
    },
  };
};

/**
 * Transform backend API response to match the format expected by the UI
 * @param {Object} backendResponse - Response from backend API
 * @param {number} decimalPlaces - Number of decimal places for rounding
 * @returns {Array} Transformed results array
 */
export function transformBackendResults(
  backendResponse,
  decimalPlaces = 2,
  geometry = null
) {
  if (!backendResponse?.Success || !backendResponse?.CalendarPeriods) {
    throw new Error(backendResponse?.ErrorMessage || "Invalid response format");
  }

  const isNullish = (value) => value === null || value === undefined;

  const mainResults = [];
  const aesResults = [];
  let hasProjectData = false;

  // compute area once and reuse for all rows
  const areaTxt = deriveArea(decimalPlaces, geometry);

  // Separate periods by group: projTS + beobTS -> mainResults, aesTS -> aesResults
  backendResponse.CalendarPeriods.forEach((period) => {
    const { Scenarios, GroupId, Unit } = period;

    if (GroupId === "beobTS") {
      // observation: one row per period
      const rowData = extractRowData(period);
      if (!isNullish(rowData.avg)) {
        mainResults.push(
          formatStatisticRow(rowData, areaTxt, decimalPlaces, {
            includeRange: false,
          })
        );
      }
    } else if (GroupId === "projTS" && Scenarios?.length > 0) {
      // projections: one row per scenario
      const label = period.CalendarPeriodId?.replace(/_/g, "-") || "";
      Scenarios.forEach((scenario) => {
        const rowData = extractRowData(scenario, {
          label,
          unit: Unit,
          projection: true,
        });
        if (!isNullish(rowData.avg)) {
          hasProjectData = true; // if at least one scenario has valid data, we will show the aesResults (change relative to beobTS / Änderungen zu 1971-2000)
          mainResults.push(formatStatisticRow(rowData, areaTxt, decimalPlaces));
        }
      });
      // Änderung zu 1971-2000
    } else if (GroupId === "aesTS") {
      // aesTS: aggregate/change values relative to reference period
      const label = period.CalendarPeriodId?.replace(/_/g, "-") || "";
      const rawLabel = `${label} zu 1971-2000`;

      // The reference period (1971-2000) shows change from itself -> always 0.
      // Display "-" instead of a numeric zero.
      const isReferencePeriod = /1971.+2000/.test(label);

      // If there are scenarios, it's projections
      if (Scenarios?.length > 0) {
        Scenarios.forEach((scenario) => {
          const rowData = extractRowData(scenario, {
            label,
            rawLabel,
            projection: true,
            unit: Unit,
          });
          if (!isNullish(rowData.avg)) {
            aesResults.push(
              formatStatisticRow(rowData, areaTxt, decimalPlaces)
            );
          }
        });
        // if there are no scenarios, it's observations
      } else {
        const rowData = extractRowData(period, { rawLabel });
        if (!isNullish(rowData.avg)) {
          const row = formatStatisticRow(rowData, areaTxt, decimalPlaces, {
            includeRange: false,
          });
          if (isReferencePeriod) {
            row.avgTxt = "-";
          }
          aesResults.push(row);
        }
      }
    }
  });

  return { mainResults, aesResults: hasProjectData ? aesResults : [] };
}

export const round = (number, decimalPlaces, numberOnly = false) => {
  if (decimalPlaces === undefined || decimalPlaces === null) return number;
  // const pow = Math.pow(10, decimalPlaces);
  if (numberOnly) {
    return _round(number, decimalPlaces);
    // return Math.round(number * pow) / pow;
  }

  return formatNumber(number, decimalPlaces);
  // let numberfix = 0;
  // let floatingString = "";
  // const onlyFloating = Math.round((number % 1) * pow) / pow;

  // if (onlyFloating === 1) {
  //   numberfix = 1;
  // } else if (onlyFloating !== 0) {
  //   floatingString = `,${`${onlyFloating}`.split(".")[1]}`;
  // }
  // let nr = "" + Math.floor(Math.abs(number) + numberfix);
  // let str = "";
  // while (nr > 1000) {
  //   str = ` ${nr % 1000}${str}`;
  //   nr = Math.floor(nr / 1000);
  // }
  // str = `${number < 0 ? "-" : ""}${nr !== 0 ? nr : ""}${str}${floatingString}`;

  // // if (str.indexOf(".") !== -1 && str.indexOf(",") !== -1) debugger;
  // return str;
};

// // TODO: Ergebnis für das Komplette Layer immer vorberechnen und in State speichern => anzeigen, wenn keine geometry ausgewählt ist!!
// export const calculateResult = (
//   url,
//   token,
//   map,
//   view,
//   valueField,
//   geometry,
//   units,
//   decimalPlaces = 2,
//   infos,
//   newBehavior = false
// ) => {
//   return new Promise((resolve, reject) => {
//     const area = geometry
//       ? round(
//           APIHelper.planarArea(geometry, "square-kilometers"),
//           decimalPlaces,
//           false
//         ) + " [km²]"
//       : " - ";
//     let unit = !units ? "" : ` [${units}]`;
//     if (newBehavior || !geometry) {
//       APIHelper.calculateStatisticsFromLayer(
//         // min, max, count, avg, median
//         url,
//         token,
//         view,
//         map,
//         valueField,
//         geometry
//       )
//         .then(({ min, max, count, med, avg }) => {
//           resolve({
//             ...infos,
//             min: _round(min, decimalPlaces),
//             minTxt: formatNumber(min, decimalPlaces) + unit,
//             max: _round(max, decimalPlaces),
//             maxTxt: formatNumber(max, decimalPlaces) + unit,
//             med: _round(med, decimalPlaces),
//             medTxt: formatNumber(med, decimalPlaces) + unit,
//             avg: _round(avg, decimalPlaces),
//             avgTxt: formatNumber(avg, decimalPlaces) + unit,
//             area,
//             count,
//           });
//         })
//         .catch((e) => {
//           console.error(e, "Error with calculating RasterStatistics");
//           reject(e);
//         });
//     } else {
//       APIHelper.queryAllFeaturesOnLayerUrl(
//         url,
//         token,
//         view,
//         map,
//         false, // x and y Data not neccesary
//         geometry, // geometric restriction
//         [valueField]
//       )
//         .then((features) => {
//           const values = features.map((feature) =>
//             parseFloat(APIHelper.getAttributes(feature)[valueField])
//           );

//           values.sort((a, b) => a - b);
//           //decimalPlaces = 3;
//           let avg = null;
//           let med = null;
//           let min = null;
//           let max = null;
//           let count = values.length;
//           if (count > 0) {
//             avg = _round(
//               values.reduce((acc, value) => {
//                 return acc + value;
//               }, 0) / count,
//               decimalPlaces
//             );
//             med = _round(
//               count % 2 === 1
//                 ? values[Math.floor(count / 2)]
//                 : (values[count / 2] + values[count / 2 - 1]) / 2,
//               decimalPlaces
//             );
//             min = _round(
//               values.reduce((acc, value) => {
//                 return acc === null || value < acc ? value : acc;
//               }, null),
//               decimalPlaces
//             );
//             max = _round(
//               values.reduce((acc, value) => {
//                 return value > acc ? value : acc;
//               }, 0),
//               decimalPlaces
//             );
//           }
//           resolve({
//             ...infos,
//             min,
//             minTxt: formatNumber(min, decimalPlaces) + unit,
//             max,
//             maxTxt: formatNumber(max, decimalPlaces) + unit,
//             med,
//             medTxt: formatNumber(med, decimalPlaces) + unit,
//             avg,
//             avgTxt: formatNumber(avg, decimalPlaces) + unit,
//             area,
//             count,
//           });
//         })
//         .catch((e) => {
//           console.error(e, "Error with calculating RasterStatistics");
//           reject(e);
//         });
//     }
//   });
// };

// export const calculateProjectedResult = (
//   urls,
//   token,
//   map,
//   view,
//   valueField,
//   geometry,
//   units,
//   decimalPlaces = 2,
//   infos = {}
// ) => {
//   const { min: minUrl, max: maxUrl, med: medUrl } = urls;
//   return new Promise((resolve, reject) => {
//     Promise.all(
//       [minUrl, medUrl, maxUrl].map((url) => {
//         return url
//           ? calculateResult(
//               url,
//               token,
//               map,
//               view,
//               valueField,
//               geometry,
//               units,
//               decimalPlaces,
//               infos,
//               false
//             )
//           : null;
//       })
//     )
//       .then(([minResult, medResult, maxResult]) => {
//         const someResult = minResult || medResult || maxResult;
//         resolve({
//           ...infos,
//           min: minResult?.avg,
//           minTxt: minResult?.avgTxt,
//           max: maxResult?.avg,
//           maxTxt: maxResult?.avgTxt,
//           med: medResult?.avg,
//           medTxt: medResult?.avgTxt,
//           avg: medResult?.avg,
//           avgTxt: medResult?.avgTxt,
//           area: someResult.area,
//           count: someResult.count,
//         });
//       })
//       .catch((e) => {
//         reject(e);
//       });
//   });
// };

// export const calculateForSelection = (
//   possibilities,
//   projection,
//   token,
//   map,
//   view,
//   valueField,
//   geometry,
//   units,
//   decimalPlaces,
//   info
// ) => {
//   return new Promise((resolve, reject) => {
//     if (projection) {
//       // wenn eins von denen fehlt einfach in der Tabelle "noData" anzeigen bzw. nls.noData
//       // debugger;
//       const urls = {
//         min: null,
//         max: null,
//         med: null,
//       };
//       for (let i = 0; i < possibilities.length; i++) {
//         const possibility = possibilities[i];
//         if (
//           typeof projection === "string" &&
//           possibility.constraints?.Szenario !== projection
//         ) {
//           continue;
//         }
//         switch (`${possibility.constraints?.Characteristic}`.toLowerCase()) {
//           case "minimum":
//             urls.min = possibility.rasterVals;
//             break;
//           case "maximum":
//             urls.max = possibility.rasterVals;
//             break;
//           case "mittel":
//             urls.med = possibility.rasterVals;
//             break;
//           default:
//         }
//       }

//       calculateProjectedResult(
//         urls,
//         token,
//         map,
//         view,
//         valueField,
//         geometry,
//         units,
//         decimalPlaces,
//         { ...info, projection: true }
//       )
//         .then((result) => {
//           resolve(result);
//         })
//         .catch((e) => reject(e));
//     } else {
//       const url = possibilities[possibilities.length - 1].rasterVals;
//       calculateResult(
//         url,
//         token,
//         map,
//         view,
//         valueField,
//         geometry,
//         units,
//         decimalPlaces,
//         { ...info, projection: false },
//         false
//       )
//         .then((result) => {
//           resolve(result);
//         })
//         .catch((e) => reject(e));
//     }
//   });
// };

// export const calculateForAllTimeSpans = ({
//   allRelevantTimespans,
//   map,
//   view,
//   decimalPlaces,
//   showFuture,
//   allSzenarios,
//   token,
//   valueField,
//   geometry,
//   units,
// }) => {
//   const promises = [];
//   allRelevantTimespans.forEach(
//     ({ short, label, projection, possibilities }) => {
//       let actualDecimalPlaces =
//         "Nachkommastellen" in possibilities?.[0]
//           ? Number(possibilities[0].Nachkommastellen)
//           : decimalPlaces;
//       if (projection) {
//         if (showFuture) {
//           const szenarioMap = getSzenarioMap(possibilities, allSzenarios);
//           Object.keys(szenarioMap).forEach((szenario) => {
//             const szenarioPossibilities = szenarioMap[szenario];
//             actualDecimalPlaces =
//               "Nachkommastellen" in szenarioPossibilities?.[0]
//                 ? Number(szenarioPossibilities[0].Nachkommastellen)
//                 : actualDecimalPlaces;
//             promises.push(
//               calculateForSelection(
//                 // calculate results for selection and consider projected ones
//                 szenarioPossibilities, // possibilities
//                 szenario, // projection
//                 token,
//                 map,
//                 view,
//                 valueField,
//                 geometry,
//                 units,
//                 actualDecimalPlaces, // decimalPlaces,
//                 {
//                   meta: {
//                     short: `${short} (${szenario})`,
//                     label: `${label} (${szenario})`,
//                     rawLabel: label,
//                     projection: szenario,
//                   },
//                 }
//               )
//             );
//           });
//         }
//       } else {
//         promises.push(
//           calculateForSelection(
//             // calculate results for selection and consider projected ones
//             possibilities,
//             false, // projection
//             token,
//             map,
//             view,
//             valueField,
//             geometry,
//             units,
//             actualDecimalPlaces,
//             {
//               meta: {
//                 short,
//                 label,
//                 rawLabel: label,
//                 projection: false,
//               },
//             }
//           )
//         );
//       }
//     }
//   );
//   return Promise.all(promises);
// };
